// AdminPage.jsx — P0.34
//
// Platform administration, moved out of Profile & Settings.
//
// WHY IT IS A SEPARATE PAGE
// Profile & Settings is where a person configures THEIR OWN environment —
// identity, company intelligence, persona names, their mailbox. Four options
// had accumulated there that configure nothing of the sort: they administer the
// deployment. Users approves other people. Logs reads infrastructure. Platform
// shows every workspace's spend. Data requests tracks "forget me" across
// tenants. A page whose subtitle is "Everything here shapes how Digital Me
// works for you" was the wrong home for all four.
//
// THE WHOLE PAGE IS SYSTEM-ADMIN ONLY, AND THAT IS A DELIBERATE NARROWING.
// Asked and answered on 25 Oct 2026. The four options carried THREE different
// gates in their old home:
//
//   Users          requireRole        owner OR admin
//   Logs           requireOwner       owner
//   Platform       requireSystemAdmin SYSTEM_ADMIN_EMAILS
//   Data requests  requireSystemAdmin SYSTEM_ADMIN_EMAILS
//
// This page shows all four to SYSTEM_ADMIN_EMAILS and nothing to anyone else.
// So a tenant owner or admin who is not a system admin LOSES the only UI route
// by which they could approve a waiting colleague or read activity logs. That
// was put to the user explicitly, with the regression named, and confirmed.
//
// NO API GATE IS TOUCHED. The APIs still permit what they permitted —
// users/pending and users/approve still accept an admin, logs/activity still
// accepts an owner. So the UI and the enforcement now DISAGREE, and the
// direction matters: the UI shows LESS than the API allows. That is the safe
// direction. The reverse — a menu offering what the API refuses — is the one
// that produces a 403 the user cannot explain. Tightening the APIs to match was
// offered and declined; it is a genuine privilege reduction (it changes who can
// approve a user) and belongs in its own deliberate change, not bundled into a
// navigation move.
//
// HIDING IS STILL A COURTESY, NOT A CONTROL. Every panel below is reached by an
// endpoint that re-checks its own gate. Rendering nothing here keeps the menu
// out of the way; it does not protect anything. tests/admin-cross-tenant-gate
// and tests/admin-usage-summary pin the enforcement side.

import React, { useState, useEffect } from 'react';
import { apiFetch } from '../lib/api';
import UserApprovalPanel from './UserApprovalPanel';
import LogViewerPanel from './LogViewerPanel';
import AdminUsagePanel from './AdminUsagePanel';
import DataRequestsPanel from './DataRequestsPanel';
import './ProfileSettingsPage.css';

// The four items, in the order they appear in the left panel and here.
// `id` is the URL segment: `#admin/Users`. Hyphenated where the label has a
// space, because a hash fragment with a space in it is not a thing a user can
// paste reliably.
export const ADMIN_SECTIONS = [
  { id: 'Users',         label: 'Users',         icon: '👥' },
  { id: 'Logs',          label: 'Logs',          icon: '📜' },
  { id: 'Platform',      label: 'Platform',      icon: '🛠️' },
  { id: 'Data-requests', label: 'Data requests', icon: '🗑️' },
];

/**
 * Section shell. Deliberately a copy of ProfileSettingsPage's, not an import
 * from it: this page must not depend on a 1,900-line settings page to render,
 * and the shared thing is the CSS (ProfileSettingsPage.css, imported above),
 * which is where a visual change belongs. Nine lines of markup is a smaller
 * liability than the coupling.
 */
function Section({ title, subtitle, children }) {
  return (
    <div className="ps-section">
      <div className="ps-section-header">
        <h2 className="ps-section-title">{title}</h2>
        {subtitle && <p className="ps-section-sub">{subtitle}</p>}
      </div>
      <div className="ps-section-body">{children}</div>
    </div>
  );
}

export default function AdminPage({ initialSection = null }) {
  // Resolved from the server, not from the JWT's role claim: system admin is an
  // address in SYSTEM_ADMIN_EMAILS — a Vercel environment variable — and not a
  // database role, so there is nothing in the token to read. /api/me/data-request
  // answers "are you one" without probing an admin endpoint for every user.
  //
  // null means UNKNOWN, distinct from false. Rendering "you do not have access"
  // during the round trip would tell a system admin they are not one, every
  // time they open the page.
  const [isSystemAdmin, setIsSystemAdmin] = useState(null);
  const [openDataRequests, setOpenDataRequests] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);

  const [activeSection, setActiveSection] = useState(() => {
    const fromUrl = (initialSection || '').trim();
    return ADMIN_SECTIONS.some(s => s.id === fromUrl) ? fromUrl : ADMIN_SECTIONS[0].id;
  });

  useEffect(() => {
    let cancelled = false;
    apiFetch('/api/me/data-request')
      .then(r => r.json())
      .then(d => { if (!cancelled) setIsSystemAdmin(Boolean(d.isSystemAdmin)); })
      // A failed probe resolves to FALSE, not null: an unknown that never
      // resolves would leave the page spinning forever. No access is the safe
      // failure, and the endpoints refuse anyway.
      .catch(() => { if (!cancelled) setIsSystemAdmin(false); });
    return () => { cancelled = true; };
  }, []);

  // Counts on the menu items, so work is visible without opening them. Both are
  // fetched only once the caller is known to be a system admin — probing them
  // for everyone would 403 in every other user's console on every page load.
  useEffect(() => {
    if (!isSystemAdmin) return;
    let cancelled = false;
    apiFetch('/api/admin/data-requests/list')
      .then(r => r.json())
      .then(d => { if (!cancelled) setOpenDataRequests(d.openCount || 0); })
      .catch(() => {});
    apiFetch('/api/admin/users/pending')
      .then(r => r.json())
      .then(d => { if (!cancelled) setPendingCount(d.count || 0); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isSystemAdmin]);

  // Keep the hash in step with the tab, so a reload or a shared link lands where
  // the user was. Only writes `#admin/<id>` — never a section this page does not
  // render, which is how a stale link used to select a tab that was not there.
  useEffect(() => {
    if (!activeSection) return;
    const want = `#admin/${activeSection}`;
    if (window.location.hash !== want) {
      window.history.replaceState(null, '', want);
    }
  }, [activeSection]);

  if (isSystemAdmin === null) {
    return (
      <div className="ps-root">
        <div className="ps-header">
          <div className="ps-header-left">
            <h1 className="ps-title">Administration</h1>
            <p className="ps-subtitle">Checking your access…</p>
          </div>
        </div>
      </div>
    );
  }

  // Someone reached #admin without being a system admin — a stale bookmark, a
  // shared link, or a session whose membership changed. Say so plainly rather
  // than rendering an empty page: a blank screen reads as a broken app, and
  // this is the one state where naming the gate is useful, because membership
  // is managed outside the product.
  if (!isSystemAdmin) {
    return (
      <div className="ps-root">
        <div className="ps-header">
          <div className="ps-header-left">
            <h1 className="ps-title">Administration</h1>
            <p className="ps-subtitle">
              This area is for the deployment's system administrators.
              {' '}<span className="ps-subtitle-emphasis">
                Access comes from an address in SYSTEM_ADMIN_EMAILS, set in the
                deployment's Vercel environment. It cannot be granted from inside the
                product, and it does not travel with a tenant swap.
              </span>
            </p>
          </div>
        </div>
      </div>
    );
  }

  const badgeFor = (id) => {
    if (id === 'Users' && pendingCount > 0) return pendingCount;
    if (id === 'Data-requests' && openDataRequests > 0) return openDataRequests;
    return null;
  };

  return (
    <div className="ps-root">
      <div className="ps-header">
        <div className="ps-header-left">
          <h1 className="ps-title">Administration</h1>
          <p className="ps-subtitle">
            Administer the deployment: who has access, what the platform is doing, and
            what it costs.
            {' '}<span className="ps-subtitle-emphasis">
              This is the whole deployment, not one workspace — Platform and Data
              requests span every tenant.
            </span>
          </p>
        </div>
      </div>

      <div className="ps-layout">
        <nav className="ps-nav">
          {ADMIN_SECTIONS.map(s => {
            const badge = badgeFor(s.id);
            return (
              <button
                key={s.id}
                className={`ps-nav-item ${activeSection === s.id ? 'ps-nav-item--active' : ''}`}
                onClick={() => setActiveSection(s.id)}
              >
                {/* ps-nav-item-icon, and a bare label — matching
                    ProfileSettingsPage's markup exactly. My first version used
                    ps-nav-icon / ps-nav-label, neither of which exists in
                    ProfileSettingsPage.css: the nav would have rendered
                    unstyled, which no test would have caught and a build would
                    not have mentioned. Checked each class against the stylesheet
                    rather than assuming the names. */}
                <span className="ps-nav-item-icon">{s.icon}</span> {s.label}
                {badge !== null && <span className="ps-nav-badge">{badge}</span>}
              </button>
            );
          })}
        </nav>

        <div className="ps-content">
          {activeSection === 'Users' && (
            <Section
              title="Users &amp; Access"
              subtitle="Approve people who have signed in and are waiting for access."
            >
              <UserApprovalPanel />
            </Section>
          )}

          {activeSection === 'Logs' && (
            <Section
              title="Logs"
              subtitle="Recent activity from Vercel, GitHub Actions, and the app itself. Read-only."
            >
              <LogViewerPanel />
            </Section>
          )}

          {activeSection === 'Platform' && (
            <Section
              title="Platform"
              subtitle="Every workspace, its people, and what the AI costs to run."
            >
              <AdminUsagePanel />
            </Section>
          )}

          {activeSection === 'Data-requests' && (
            <Section
              title="Data requests"
              subtitle="“Forget me” requests from every workspace, tracked to closure."
            >
              <DataRequestsPanel onCountChange={setOpenDataRequests} />
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}
